import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Cargo } from '@eleg360/types';
import { categoriasApi, type ModuloCategoria } from '@/lib/api/core';
import { adminApi } from '@/lib/api/admin';
import { CARGOS, CARGO_PADRAO, ehCargo, useSalvarCargo } from '@/lib/escala';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { toast } from '@/lib/stores/toastStore';
import { paraE164, formatarTelefone } from '@/lib/format';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

const ROTULO_MODULO: Record<ModuloCategoria, string> = {
  solicitacao: 'Solicitações',
  acao: 'Ações Parlamentares',
  agenda: 'Agenda / Eventos',
  contato: 'Tags de contato',
};

export function ConfigPage() {
  const { bairros } = useBairros();
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<0 | 1 | 2 | 3>(0); // 0 = INFO, 1 = VOTOS, 2 = Integração, 3 = Tipos & Notificações

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => categoriasApi.list(token),
    enabled: !!token,
  });

  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
  });

  const invalidarCategorias = () => queryClient.invalidateQueries({ queryKey: ['categorias'] });

  // Mostrado como marca d'agua no campo vazio: sem isso, "em branco" nao diz
  // qual prazo a categoria acaba tendo.
  const slaPadrao = mandato?.configuracoes?.sla_padrao_dias ?? 30;

  const criarCategoria = useMutation({
    mutationFn: (dados: { nome: string; modulo: ModuloCategoria; sla_dias?: number }) =>
      categoriasApi.create(dados, token),
    onSuccess: () => {
      invalidarCategorias();
      toast('Categoria cadastrada.');
      setNovaCatNome('');
      setNovaCatSla('');
    },
    // O servidor recusa nome repetido dentro do mesmo módulo
    onError: (err: any) => toast(erroDe(err, 'Erro ao cadastrar a categoria.')),
  });

  const atualizarCategoria = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: { sla_dias?: number | null } }) =>
      categoriasApi.update(id, dados, token),
    onSuccess: (cat) => {
      invalidarCategorias();
      toast(
        cat.sla_dias === null
          ? `"${cat.nome}" voltou ao prazo padrão do mandato.`
          : `"${cat.nome}": prazo de ${cat.sla_dias} dia(s).`,
      );
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao alterar o prazo da categoria.')),
  });

  const removerCategoria = useMutation({
    mutationFn: (id: string) => categoriasApi.delete(id, token),
    onSuccess: () => {
      invalidarCategorias();
      toast('Categoria removida.');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a categoria.')),
  });

  const salvarTelefones = useMutation({
    mutationFn: (configuracoes: any) => adminApi.updateMandato({ configuracoes }, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mandato'] });
      toast('Números autorizados de notificação atualizados.');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao salvar os números.')),
  });

  // Mutação compartilhada com o SeletorEscala do topbar — ver comentário em
  // lib/escala.ts.
  const salvarCargo = useSalvarCargo();

  const cargoAtual: Cargo = ehCargo(mandato?.cargo) ? mandato.cargo : CARGO_PADRAO;

  const [novaCatNome, setNovaCatNome] = useState('');
  const [novaCatModulo, setNovaCatModulo] = useState<ModuloCategoria>('solicitacao');
  const [novaCatSla, setNovaCatSla] = useState('');
  const [telChefe, setTelChefe] = useState('');
  const [telParlamentar, setTelParlamentar] = useState('');

  useEffect(() => {
    if (!mandato) return;
    setTelChefe(formatarTelefone(mandato.configuracoes?.notif_telefone_chefe) || '');
    setTelParlamentar(formatarTelefone(mandato.configuracoes?.notif_telefone_parlamentar) || '');
  }, [mandato?.updated_at]);

  const handleSalvarTelefones = () => {
    const chefe = telChefe.trim() ? paraE164(telChefe) : undefined;
    const parlamentar = telParlamentar.trim() ? paraE164(telParlamentar) : undefined;

    if (telChefe.trim() && !chefe) {
      toast('Telefone do chefe de gabinete inválido.');
      return;
    }
    if (telParlamentar.trim() && !parlamentar) {
      toast('Telefone do parlamentar inválido.');
      return;
    }
    salvarTelefones.mutate({
      notif_telefone_chefe: chefe,
      notif_telefone_parlamentar: parlamentar,
    });
  };

  // Estado INFO
  const [info, setInfo] = useState({
    nome: 'Ana Silva',
    urna: 'Ana Silva',
    cargo: 'Vereadora',
    partido: 'PXX',
    numero: '77123',
    mun: 'Fortaleza',
    uf: 'CE',
    mandato: '2025–2028',
  });

  // Estado VOTOS
  const [votos, setVotos] = useState({
    abr: 'Municipal',
    cargo: 'Vereador',
    ano: '2024',
    cand: 'cand_01J7R4Q',
  });

  const [searchCand, setSearchCand] = useState('Ana Silva');
  const [isSearchCandOpen, setIsSearchCandOpen] = useState(false);

  const CANDIDATOS_TSE = [
    { id: 'cand_01J7R4Q', n: 'ANA SILVA', urna: 'ANA SILVA', num: '77123', part: 'PXX', mun: 'Fortaleza', uf: 'CE', el: '2024 · Vereador', votos: 8412 },
    { id: 'cand_01K2M8B', n: 'ANA PAULA SILVA', urna: 'ANA SILVA', num: '55321', part: 'PYY', mun: 'Caucaia', uf: 'CE', el: '2024 · Vereador', votos: 2110 },
    { id: 'cand_00H9Z1C', n: 'ANA CRISTINA DA SILVA', urna: 'ANINHA SILVA', num: '44', part: 'PZZ', mun: 'Fortaleza', uf: 'CE', el: '2020 · Vereador', votos: 980 },
  ];

  return (
    <div className="page" id="p-config">
      {/* Head */}
      <div className="head">
        <div>
          <h1>Configuração do gabinete</h1>
          <p>
            INFO (identidade do mandato), VOTOS (abrangência e candidato de referência), Integração (fontes) e Tipos/Categorias com limite de notificações WhatsApp (até 2 números).
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={tab === 0 ? 'on' : ''}
          onClick={() => setTab(0)}
        >
          INFO
        </button>
        <button
          className={tab === 1 ? 'on' : ''}
          onClick={() => setTab(1)}
        >
          VOTOS
        </button>
        <button
          className={tab === 2 ? 'on' : ''}
          onClick={() => setTab(2)}
        >
          Integração
        </button>
        <button
          className={tab === 3 ? 'on' : ''}
          onClick={() => setTab(3)}
        >
          Tipos &amp; Notificações
        </button>
      </div>

      {/* ABA 0: INFO */}
      {tab === 0 && (
        <>
          <div className="grid g3">
            {(
              [
                ['Nome político', 'nome'],
                ['Nome de urna', 'urna'],
                ['Função / cargo', 'cargo'],
                ['Partido', 'partido'],
                ['Número eleitoral', 'numero'],
                ['Município', 'mun'],
                ['UF', 'uf'],
                ['Mandato', 'mandato'],
              ] as const
            ).map(([l, k]) => (
              <div key={k} className="card">
                <div className="field">
                  <label>{l}</label>
                  <input
                    className="in"
                    value={info[k]}
                    onChange={(e) =>
                      setInfo({ ...info, [k]: e.target.value })
                    }
                  />
                </div>
              </div>
            ))}

            <div className="card">
              <div className="field">
                <label htmlFor="cfg-cargo">Cargo do mandato</label>
                <select
                  id="cfg-cargo"
                  className="in"
                  value={cargoAtual}
                  disabled={!mandato || salvarCargo.isPending}
                  onChange={(e) => salvarCargo.mutate(e.target.value as Cargo)}
                >
                  {(Object.keys(CARGOS) as Cargo[]).map((k) => (
                    <option key={k} value={k}>
                      {CARGOS[k].rotulo}
                    </option>
                  ))}
                </select>
                <div className="d" style={{ marginTop: 6, fontSize: 11, color: 'var(--ink3)' }}>
                  Define qual camada do território é a unidade de trabalho. Hoje as telas chamam
                  essa unidade de <b>{CARGOS[cargoAtual].unidade}</b>.
                </div>
              </div>
            </div>

            <div className="card">
              <div className="field">
                <label>Tema visual</label>
                <div className="filters" style={{ marginTop: 6 }}>
                  <span className="chip acc chipbtn on">Azul institucional</span>
                  <span className="chip eleg chipbtn">Violeta</span>
                  <span className="chip good chipbtn">Verde</span>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="field">
                <label>Logo do gabinete</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
                  <span className="logo" style={{ width: 40, height: 40 }} />
                  <button className="btn sm" onClick={() => alert('Upload de logo institucional')}>
                    Trocar
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>Administrador</h2>
            <div className="grid g3" style={{ marginTop: 8 }}>
              <div className="field">
                <label>Nome</label>
                <input className="in" defaultValue="Marcos Cunha" />
              </div>
              <div className="field">
                <label>E-mail</label>
                <input className="in" defaultValue="chefia@gab-silva.leg.br" />
              </div>
              <div className="field">
                <label>WhatsApp (canal interno)</label>
                <input className="in" defaultValue="(85) 9 8xxx-xx01" />
              </div>
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 8 }}>
              CPF não é coletado, por minimização de dados (decisão de arquitetura, alinhada aos princípios de privacidade da LGPD).
            </p>
          </div>
        </>
      )}

      {/* ABA 1: VOTOS */}
      {tab === 1 && (
        <>
          <div className="grid g3">
            <div className="card">
              <div className="field">
                <label>Abrangência</label>
                <select
                  className="in"
                  value={votos.abr}
                  onChange={(e) => setVotos({ ...votos, abr: e.target.value })}
                >
                  <option>Municipal</option>
                  <option>Estadual</option>
                  <option>Federal</option>
                </select>
              </div>
            </div>

            <div className="card">
              <div className="field">
                <label>Cargo</label>
                <select
                  className="in"
                  value={votos.cargo}
                  onChange={(e) => setVotos({ ...votos, cargo: e.target.value })}
                >
                  <option>Vereador</option>
                  <option>Prefeito</option>
                  <option>Deputado estadual</option>
                  <option>Deputado federal</option>
                  <option>Senador</option>
                  <option>Governador</option>
                  <option>Presidente</option>
                </select>
              </div>
            </div>

            <div className="card">
              <div className="field">
                <label>Eleição de referência</label>
                <select
                  className="in"
                  value={votos.ano}
                  onChange={(e) => setVotos({ ...votos, ano: e.target.value })}
                >
                  <option>2024</option>
                  <option>2020</option>
                  <option>2016</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>
              Candidato de referência{' '}
              {votos.cand ? (
                <span className="chip good">vinculado · {votos.cand}</span>
              ) : (
                <span className="chip warn">não vinculado</span>
              )}
            </h2>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                className="in"
                value={searchCand}
                onChange={(e) => setSearchCand(e.target.value)}
                placeholder="Pesquisar candidato por nome (TSE/ELEG)"
              />
              <button className="btn pri" onClick={() => setIsSearchCandOpen(true)}>
                Pesquisar
              </button>
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 8 }}>
              A vinculação exige validação manual quando houver homônimos. Nunca é automática (INT-ELE-003/004).
            </p>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>Territórios de atuação</h2>
            <div className="filters" style={{ marginTop: 8 }}>
              {bairros.map((b) => (
                <span
                  key={b.id}
                  className="chip acc chipbtn on"
                  onClick={(e) => e.currentTarget.classList.toggle('on')}
                >
                  {b.n}
                </span>
              ))}
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 8 }}>
              Unidades territoriais consideradas nos indicadores. Desmarcar exclui do cálculo, não apaga dados.
            </p>
          </div>
        </>
      )}

      {/* ABA 2: INTEGRAÇÃO */}
      {tab === 2 && (
        <div className="card flat tw">
          <table>
            <thead>
              <tr>
                <th>Fonte</th>
                <th>Identificação do registro</th>
                <th>Última atualização</th>
                <th>Status</th>
                <th>Logs</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <b>TSE · dados abertos</b>
                </td>
                <td className="mono">{votos.cand || '— (vincule o candidato)'}</td>
                <td>18/08/2026 02:41</td>
                <td>
                  <span className={`chip ${votos.cand ? 'good' : 'warn'}`}>
                    {votos.cand ? 'ativa' : 'pendente de vinculação'}
                  </span>
                </td>
                <td>
                  <Link to="/integracoes">ver cargas</Link>
                </td>
              </tr>
              <tr>
                <td>
                  <b>ELEG · base licenciada</b>
                </td>
                <td className="mono">eleg://cand/01J7R4Q</td>
                <td>15/08/2026</td>
                <td>
                  <span className="chip good">ativa · licença até 12/2026</span>
                </td>
                <td>
                  <Link to="/integracoes">ver cargas</Link>
                </td>
              </tr>
              <tr>
                <td>
                  <b>IBGE · Censo 2022</b>
                </td>
                <td className="mono">setores CE</td>
                <td>15/08/2026</td>
                <td>
                  <span className="chip good">ativa</span>
                </td>
                <td>—</td>
              </tr>
              <tr>
                <td>
                  <b>Percepção pública</b>
                </td>
                <td className="mono">3 fontes autorizadas</td>
                <td>21/08/2026</td>
                <td>
                  <span className="chip warn">1 fonte desatualizada (9 dias)</span>
                </td>
                <td>—</td>
              </tr>
            </tbody>
          </table>

          <div className="note warn" style={{ marginTop: 14 }}>
            Fonte indisponível ou desatualizada vira aviso na tela analítica, nunca dado
            apresentado como atual.
            <Info titulo="Regra INT-ELE-010">
              INT-ELE-010: nenhuma tela analítica apresenta como atual um número vindo de fonte
              indisponível ou desatualizada; ela exibe o aviso no lugar.
            </Info>
          </div>
        </div>
      )}

      {/* ABA 3: TIPOS, CATEGORIAS & NOTIFICAÇÕES WHATSAPP */}
      {tab === 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Configuração de Notificações WhatsApp (Máximo 2 Números) */}
          <div className="card">
            <h2>Notificações do Gabinete · WhatsApp</h2>
            <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 4 }}>
              No máximo 2 números do gabinete recebem notificação automática: Chefe de Gabinete e
              Parlamentar/Responsável.
            </p>

            <div className="grid g2" style={{ marginTop: 14 }}>
              <div className="field">
                <label>Telefone do Chefe de Gabinete</label>
                <input
                  className="in"
                  placeholder="+55 85 98888-0000"
                  value={telChefe}
                  onChange={(e) => setTelChefe(e.target.value)}
                />
              </div>
              <div className="field">
                <label>Telefone do Parlamentar / Responsável</label>
                <input
                  className="in"
                  placeholder="+55 85 99999-0000"
                  value={telParlamentar}
                  onChange={(e) => setTelParlamentar(e.target.value)}
                />
              </div>
            </div>

            <button
              className="btn pri"
              style={{ marginTop: 12 }}
              onClick={handleSalvarTelefones}
              disabled={salvarTelefones.isPending}
            >
              {salvarTelefones.isPending ? 'Salvando...' : 'Salvar Números de Notificação'}
            </button>
          </div>

          {/* Gerenciamento Dinâmico de Tipos e Categorias */}
          <div className="card">
            <h2>Tipos e Categorias de Demandas e Ações</h2>
            <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 4 }}>
              Cadastre e personalize categorias para solicitações, ações e agendas (ex: demandas agrícolas como tratores e sementes, saúde, infraestrutura).
            </p>

            {/* Formulário de Adicionar Categoria */}
            <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: '1 1 260px' }}>
                <label>Nome da Categoria / Tipo</label>
                <input
                  className="in"
                  placeholder="Ex: Agricultura / Tratores e Sementes"
                  value={novaCatNome}
                  onChange={(e) => setNovaCatNome(e.target.value)}
                />
              </div>
              <div className="field" style={{ width: 180 }}>
                <label>Módulo Aplicável</label>
                <select
                  className="in"
                  value={novaCatModulo}
                  onChange={(e) => setNovaCatModulo(e.target.value as any)}
                >
                  {(Object.keys(ROTULO_MODULO) as ModuloCategoria[]).map((m) => (
                    <option key={m} value={m}>
                      {ROTULO_MODULO[m]}
                    </option>
                  ))}
                </select>
              </div>
              {novaCatModulo === 'solicitacao' && (
                <div className="field" style={{ width: 150 }}>
                  <label>Prazo (dias)</label>
                  <input
                    className="in num"
                    type="number"
                    min={1}
                    placeholder="padrão"
                    value={novaCatSla}
                    onChange={(e) => setNovaCatSla(e.target.value)}
                  />
                  <small>Em branco usa o padrão do mandato.</small>
                </div>
              )}
              <button
                className="btn pri"
                disabled={criarCategoria.isPending}
                onClick={() => {
                  if (!novaCatNome.trim()) {
                    toast('Informe o nome da categoria.');
                    return;
                  }
                  const dias = Number(novaCatSla);
                  if (novaCatSla.trim() && (!Number.isInteger(dias) || dias < 1)) {
                    toast('O prazo deve ser um número inteiro de dias, a partir de 1.');
                    return;
                  }
                  criarCategoria.mutate({
                    nome: novaCatNome.trim(),
                    modulo: novaCatModulo,
                    // Só vai o prazo quando o módulo o comporta e ele foi informado.
                    sla_dias: novaCatModulo === 'solicitacao' && novaCatSla.trim() ? dias : undefined,
                  });
                }}
              >
                {criarCategoria.isPending ? 'Salvando...' : '+ Adicionar Categoria'}
              </button>
            </div>

            {/* Lista de Categorias Ativas */}
            <div className="tw" style={{ marginTop: 16 }}>
              <table>
                <thead>
                  <tr>
                    <th>Nome da Categoria / Tipo</th>
                    <th>Módulo</th>
                    <th style={{ width: 150 }}>Prazo (SLA)</th>
                    <th style={{ width: 80, textAlign: 'center' }}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {categorias.map((cat) => (
                    <tr key={cat.id}>
                      <td>
                        <b>{cat.nome}</b>
                      </td>
                      <td>
                        <span className="chip neutral">{ROTULO_MODULO[cat.modulo] ?? cat.modulo}</span>
                      </td>
                      <td>
                        {cat.modulo === 'solicitacao' ? (
                          <input
                            className="in num"
                            type="number"
                            min={1}
                            style={{ width: 110 }}
                            placeholder={`padrão (${slaPadrao})`}
                            defaultValue={cat.sla_dias ?? ''}
                            title="Em branco volta ao prazo padrão do mandato"
                            onBlur={(e) => {
                              const texto = e.target.value.trim();
                              const dias = texto === '' ? null : Number(texto);
                              // null é o caminho de volta ao padrão; sem ele o
                              // gabinete conseguiria personalizar e nunca desfazer.
                              if (dias !== null && (!Number.isInteger(dias) || dias < 1)) {
                                toast('O prazo deve ser um número inteiro de dias, a partir de 1.');
                                e.target.value = String(cat.sla_dias ?? '');
                                return;
                              }
                              if (dias === (cat.sla_dias ?? null)) return;
                              atualizarCategoria.mutate({ id: cat.id, dados: { sla_dias: dias } });
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn sm"
                          style={{ color: 'var(--crit)' }}
                          disabled={removerCategoria.isPending}
                          onClick={() => {
                            if (window.confirm(`Remover a categoria "${cat.nome}"?`)) {
                              removerCategoria.mutate(cat.id);
                            }
                          }}
                        >
                          Excluir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Drawer: Busca de Candidatos TSE */}
      <Drawer
        aberto={isSearchCandOpen}
        titulo="Pesquisar candidato · TSE/ELEG"
        aoFechar={() => setIsSearchCandOpen(false)}
      >
        <div className="field">
          <label>Nome pesquisado</label>
          <input
            className="in"
            value={searchCand}
            onChange={(e) => setSearchCand(e.target.value)}
          />
        </div>

        <div className="note warn" style={{ margin: '10px 0' }}>
          <b>3 registros encontrados, com homônimos.</b> Selecione o registro correto: a
          associação automática está bloqueada por ambiguidade.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {CANDIDATOS_TSE.map((c) => (
            <div
              key={c.id}
              className="card flat"
              style={{ padding: 10, background: 'var(--surface2)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <b>{c.n}</b>
                <span className="chip neutral">urna: {c.urna}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink2)', margin: '4px 0' }}>
                nº {c.num} · {c.part} · {c.mun}/{c.uf} · {c.el} · {c.votos.toLocaleString('pt-BR')} votos
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink3)' }}>
                {c.id}
              </div>
              <button
                className="btn sm pri"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setVotos({ ...votos, cand: c.id });
                  setIsSearchCandOpen(false);
                  alert(`Vinculado: ${c.n} (${c.id}) · histórico eleitoral sincronizado!`);
                }}
              >
                Validar e vincular este registro
              </button>
            </div>
          ))}
        </div>
      </Drawer>
    </div>
  );
}
